/** Play 슬롯 — the stock of Play Console app records made ahead of time (S8,
 *  `/playslots`).
 *
 *  Four buttons here reach the Play Console through browser-saas: 지금 실행
 *  makes real app records, 시험 실행 walks the same path without the final
 *  click, 다시 확인 searches the console's app list, 찾아서 등록 does the same
 *  search for one package. None of them is pressed in a dev server; the tests
 *  mock the api. */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Inbox, Pencil } from 'lucide-react'
import {
  api,
  type PlaySlot,
  type PlaySlotAttempt,
  type PlaySlotStatus,
  type PlaySlotsRunReport,
} from '@/api/client'
import { qk, usePlaySlots } from '@/lib/queries'
import { fromUnix, formatRelative, formatShort } from '@/lib/time'
import { statusOf, TONE_CLASS } from '@/lib/status'
import { cn } from '@/lib/utils'
import {
  Button,
  ConfirmDialog,
  DataTable,
  DateTimeField,
  Dialog,
  Disclosure,
  EmptyState,
  ErrorState,
  Field,
  IconButton,
  Input,
  PageHeader,
  Select,
  StatTile,
  StatusPill,
  When,
  errorText,
  useToast,
  type Column,
} from '@/ui'
import { Link } from 'react-router-dom'

const STATUSES: PlaySlotStatus[] = ['created', 'linked', 'submitted', 'live', 'abandoned']

const TRIGGER_TITLE = (r: PlaySlotsRunReport) => (r.trigger === 'verify' ? '다시 확인' : r.dry_run ? '시험 실행' : '실행')

const seconds = (ms: number) => `${(ms / 1000).toFixed(1)}초`

export default function PlaySlots() {
  const qc = useQueryClient()
  const toast = useToast()
  const q = usePlaySlots()
  const [report, setReport] = useState<PlaySlotsRunReport | null>(null)
  const [runError, setRunError] = useState<unknown>(null)
  const [confirming, setConfirming] = useState(false)
  const [editing, setEditing] = useState<PlaySlot | null>(null)

  const settle = {
    onMutate: () => {
      setReport(null)
      setRunError(null)
    },
    onSuccess: (r: PlaySlotsRunReport) => setReport(r),
    onError: (e: Error) => {
      setRunError(e)
      toast.error(e.message)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: qk.playSlots }),
  }
  const run = useMutation({ mutationFn: (dry: boolean) => api.playSlotsRun(dry), ...settle })
  const verify = useMutation({ mutationFn: () => api.playSlotsVerify(), ...settle })

  const sum = q.data?.summary
  const last = sum?.last_attempt
  const blocked = !!sum?.blocked_reason
  const remaining = sum ? Math.max(0, sum.per_day - sum.created_today) : 0
  const busy = run.isPending || verify.isPending
  const pendingDry = run.isPending && run.variables === true
  const pendingReal = run.isPending && run.variables === false

  const slotColumns: Column<PlaySlot>[] = [
    {
      key: 'pkg',
      header: '패키지',
      card: 'title',
      cell: (s) => (
        <span className="font-mono text-meta">
          {s.package_name}
          {s.notes && <span className="ml-2 font-sans text-muted-foreground">({s.notes})</span>}
        </span>
      ),
    },
    { key: 'name', header: '임시 이름', card: 'sub', cell: (s) => s.app_name },
    { key: 'status', header: '상태', card: 'meta', cell: (s) => <StatusPill domain="slot" status={s.status} /> },
    {
      key: 'created',
      header: '만든 시각',
      cell: (s) => <When iso={fromUnix(s.created_at)} mode="absolute" className="tabular-nums text-muted-foreground" />,
    },
    {
      key: 'linked',
      header: '연결된 앱',
      cardHide: (s) => !s.linked_slug,
      cell: (s) => (s.linked_slug ? <span className="font-mono text-meta">{s.linked_slug}</span> : <span className="text-muted-foreground">—</span>),
    },
    {
      key: 'edit',
      header: <span className="sr-only">편집</span>,
      align: 'right',
      // a card is one tap (and Enter) to the same dialog: no row for one icon
      card: 'hidden',
      cell: (s) => (
        // the row opens the same dialog; the icon is there for the keyboard
        <IconButton icon={Pencil} size="sm" variant="quiet" label={`${s.package_name} 연결 편집`} onClick={() => setEditing(s)} />
      ),
    },
  ]

  const attemptColumns: Column<PlaySlotAttempt>[] = [
    { key: 'at', header: '시각', card: 'hidden', cell: (a) => <When iso={fromUnix(a.at)} mode="absolute" className="tabular-nums text-muted-foreground" /> },
    {
      // the phone's line under the package: when, and what kind of attempt
      key: 'atKind',
      header: '시각 · 종류',
      card: 'sub',
      cardOnly: true,
      cell: (a) => (
        <span>
          <When iso={fromUnix(a.at)} mode="absolute" className="tabular-nums" /> · {statusOf('attemptKind', a.kind).label}
        </span>
      ),
    },
    { key: 'pkg', header: '패키지', card: 'title', cell: (a) => <span className="font-mono text-meta">{a.package_name}</span> },
    { key: 'kind', header: '종류', card: 'hidden', cell: (a) => statusOf('attemptKind', a.kind).label },
    { key: 'result', header: '결과', card: 'meta', cell: (a) => <StatusPill domain="attemptResult" status={a.result} /> },
    {
      key: 'msg',
      header: '메시지',
      className: 'max-w-md',
      cardHide: (a) => !a.message,
      cell: (a) => <span className="whitespace-pre-wrap break-words text-muted-foreground">{a.message || '—'}</span>,
    },
    {
      key: 'dur',
      header: '소요',
      align: 'right',
      cell: (a) => <span className="tabular-nums">{seconds(a.duration_ms)}</span>,
    },
  ]

  return (
    <>
      <PageHeader
        title="Play 슬롯"
        description="범용 패키지 이름으로 미리 만들어 둔 Play Console 앱 레코드. 한도에 닿으면 7일간 콘솔에 손대지 않는다."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => verify.mutate()}
              disabled={busy}
              loading={verify.isPending}
              title="확인 안 된 슬롯을 앱 목록에서 다시 검색한다. 만들지는 않고, 7일 잠금 중에도 된다."
            >
              {verify.isPending ? '확인 중 (슬롯 하나에 약 10초)' : '다시 확인'}
            </Button>
            <Button onClick={() => run.mutate(true)} disabled={busy} loading={pendingDry} title="실행과 같은 길을 가되 만들기 버튼은 누르지 않는다.">
              {pendingDry ? '시험 실행 중 (최대 1분)' : '시험 실행'}
            </Button>
            <Button
              variant="primary"
              onClick={() => setConfirming(true)}
              disabled={busy || !sum || blocked}
              loading={pendingReal}
              title={blocked ? sum?.blocked_reason : undefined}
            >
              {pendingReal ? '실행 중 (슬롯 하나에 30~40초)' : '지금 실행'}
            </Button>
          </div>
        }
      />

      {blocked && sum && <p className="-mt-2 mb-4 text-meta text-muted-foreground">지금 실행은 막혀 있다: {sum.blocked_reason}</p>}

      {q.error ? (
        <ErrorState title="Play 슬롯을 불러오지 못했다" error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <div className="space-y-5">
          {runError ? <ErrorState title="실행하지 못했다" error={runError} /> : null}
          {report && <ReportCard report={report} />}

          <section aria-label="요약" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatTile
              label="오늘 / 최근 7일 생성"
              value={`${sum?.created_today ?? '…'} / ${sum?.created_7d ?? '…'}`}
              hint={sum && `하루 ${sum.per_day}개 · 7일 ${sum.per_week}개까지`}
            />
            <StatTile label="사용 안 한 슬롯" value={sum?.unused ?? '…'} />
            <StatTile
              label="다음 시도 가능"
              // "5일 뒤" fits a phone tile; the clock goes under it and the
              // reason is said once, in the line above the tiles
              value={blocked ? formatRelative(fromUnix(sum?.next_allowed_at)) : sum ? '지금' : '…'}
              tone={blocked ? 'publishing' : undefined}
              hint={blocked ? formatShort(fromUnix(sum?.next_allowed_at)) : sum && `오늘 ${remaining}개 남음`}
            />
            <StatTile
              label="마지막 결과"
              value={last ? statusOf('attemptResult', last.result).label : '없음'}
              tone={last ? statusOf('attemptResult', last.result).tone : undefined}
              hint={last ? `${formatShort(fromUnix(last.at))} · ${last.message}` : undefined}
            />
          </section>

          {sum && !sum.enabled && (
            <p className="text-meta text-muted-foreground">
              매일 자동 실행은 꺼져 있다. 지금 실행은 꺼져 있어도 동작하고, 하루 {sum.per_day}개 할당량과 정지 규칙은 그대로다. 매일
              돌리려면 <Link to="/settings?tab=playslots" className="text-accent underline-offset-4 hover:underline">설정의 Play 슬롯 그룹</Link>에서 Enabled를 켠다.
            </p>
          )}

          <section>
            <h2 className="mb-2 text-section font-semibold">
              슬롯
              {q.data && <span className="ml-2 text-table font-normal text-muted-foreground">{q.data.slots.length}개</span>}
            </h2>
            <DataTable
              label="슬롯"
              columns={slotColumns}
              rows={q.data?.slots}
              rowKey={(s) => s.id}
              loading={q.isLoading}
              onRowClick={(s) => setEditing(s)}
              empty={<EmptyState icon={Inbox} title="아직 슬롯이 없다" description="지금 실행으로 만들거나 아래에서 손으로 만든 슬롯을 등록한다." />}
            />
          </section>

          <AdoptForm />

          <section>
            <h2 className="mb-2 text-section font-semibold">
              시도 기록<span className="ml-2 text-table font-normal text-muted-foreground">최근 50건</span>
            </h2>
            <DataTable
              label="시도 기록"
              columns={attemptColumns}
              rows={q.data?.attempts}
              rowKey={(a) => a.id}
              loading={q.isLoading}
              empty={<EmptyState title="기록이 없다" />}
            />
          </section>
        </div>
      )}

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Play Console에 앱 레코드를 만들까"
        description={`최대 ${remaining}개(오늘 남은 할당량)를 실제로 만든다. 오류가 나면 그 자리에서 멈춘다.`}
        confirmLabel="지금 실행"
        onConfirm={() => {
          setConfirming(false)
          run.mutate(false)
        }}
      />
      <SlotDialog slot={editing} onClose={() => setEditing(null)} />
    </>
  )
}

function ReportCard({ report }: { report: PlaySlotsRunReport }) {
  return (
    <section aria-label="실행 결과" className="space-y-2 rounded-lg border border-border bg-card p-4">
      <h2 className="text-section font-semibold">
        {TRIGGER_TITLE(report)} 결과<span className="ml-2 text-table font-normal text-muted-foreground">시도 {report.attempts.length}건</span>
      </h2>
      {report.blocked && <p className="text-body text-muted-foreground">막힘: {report.blocked}</p>}
      {report.stopped_reason && <p className={cn('text-body', TONE_CLASS.failed.text)}>중단: {report.stopped_reason}</p>}
      {report.attempts.length > 0 && (
        <ul className="space-y-1.5">
          {report.attempts.map((a, i) => (
            <li key={i} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-meta">
              <span className="font-mono">{a.package_name}</span>
              <span className="text-muted-foreground">{statusOf('attemptKind', a.kind).label}</span>
              <StatusPill domain="attemptResult" status={a.result} />
              <span className="min-w-0 break-words text-muted-foreground">{a.message}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/** A slot's status and its linked flutter-apps folder, in one dialog. */
function SlotDialog({ slot, onClose }: { slot: PlaySlot | null; onClose: () => void }) {
  return slot ? <SlotForm key={slot.id} slot={slot} onClose={onClose} /> : null
}

function SlotForm({ slot, onClose }: { slot: PlaySlot; onClose: () => void }) {
  const qc = useQueryClient()
  const toast = useToast()
  const [slug, setSlug] = useState(slot.linked_slug ?? '')
  const [status, setStatus] = useState<PlaySlotStatus>(slot.status)
  const save = useMutation({
    mutationFn: () => api.playSlotsPatch(slot.id, { status, linked_slug: slug.trim() }),
    onSuccess: () => {
      toast.success(`${slot.package_name}을 저장했다`)
      qc.invalidateQueries({ queryKey: qk.playSlots })
      onClose()
    },
    onError: (e: Error) => toast.error(`저장하지 못했다: ${errorText(e)}`),
  })
  return (
    <Dialog
      open
      onClose={onClose}
      title="슬롯 편집"
      description={slot.package_name}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            저장
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="상태">
          <Select value={status} onChange={(e) => setStatus(e.target.value as PlaySlotStatus)}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {statusOf('slot', s).label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="연결된 앱" hint="flutter-apps 폴더 이름. 비우면 연결을 푼다.">
          <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="flutter-apps 폴더 이름" className="font-mono" />
        </Field>
      </div>
    </Dialog>
  )
}

/** A slot someone made by hand in the console goes into the ledger. The
 *  server searches the console's app list first and refuses a package it
 *  cannot find. */
function AdoptForm() {
  const qc = useQueryClient()
  const toast = useToast()
  const [pkg, setPkg] = useState('')
  const [name, setName] = useState('')
  const [made, setMade] = useState<string | null>(null)
  const [msg, setMsg] = useState('')
  const adopt = useMutation({
    mutationFn: () =>
      api.playSlotsAdopt({
        package_name: pkg.trim(),
        app_name: name.trim(),
        created_at: made ? Math.floor(new Date(made).getTime() / 1000) : undefined,
      }),
    onMutate: () => setMsg(''),
    onSuccess: (s) => {
      setMsg(`${s.package_name}을 등록했다`)
      toast.success(`${s.package_name}을 등록했다`)
      setPkg('')
      setName('')
      setMade(null)
    },
    onError: (e: Error) => setMsg(e.message),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.playSlots }),
  })
  return (
    <Disclosure title="손으로 만든 슬롯 등록" hint="콘솔에서 직접 만든 앱 레코드를 원장에 넣는다">
      <p className="text-meta text-muted-foreground">앱 목록에서 찾은 것만 등록되고, 만든 시각은 7일 생성 수에 들어간다.</p>
      <div className="grid gap-3 md:grid-cols-3">
        <Field label="패키지">
          <Input className="font-mono" placeholder="com.pentarab.olive55" value={pkg} onChange={(e) => setPkg(e.target.value)} />
        </Field>
        <Field label="콘솔 앱 이름">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <DateTimeField label="만든 시각" value={made} onChange={setMade} hint="비우면 지금" />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => adopt.mutate()} disabled={!pkg.trim() || !name.trim()} loading={adopt.isPending}>
          {adopt.isPending ? '앱 목록에서 찾는 중' : '찾아서 등록'}
        </Button>
        {msg && <span role="status" className="text-meta text-muted-foreground">{msg}</span>}
      </div>
    </Disclosure>
  )
}
